<template>
  <div class="question" :class="questionContainerClasses">
    <div class="question__content" :class="videoContentClasses">
      <div
        v-if="showIframe"
        class="question__video embed-responsive embed-responsive-16by9"
        :style="questionFrameWrapperStyling"
      >
        <iframe
          class="question__video-iframe embed-responsive-item"
          :src="iframeSource"
          frameborder="0"
          webkitallowfullscreen
          mozallowfullscreen
          allowfullscreen
        >
        </iframe>
      </div>

      <div v-if="question.questionImage" class="question__content-image">
        <img :src="question.questionImage" alt="" />
      </div>
      <div class="question__back"><i class="icon-back question__back-icon"></i></div>

      <RichTextRenderer tag="div" class="question__wrapper" :html="question.question" />

      <QuizAnswersItems
        v-if="question.answerLayout?.state"
        :question="question"
        :is-error="isError"
        :is-focusable="isFocusable"
        :selected-answers="selectedAnswers"
        :show-other-peoples-answers="showOthersAnswers"
        :show-correct-answer="showTheCorrectAnswer"
        :general-settings="generalSettings"
        @selectAnswer="onAnswerSelected"
        @submitAnswer="onSubmitAnswer"
      />

      <h5 v-if="question.link.enabled" class="question__link-heading">
        <a class="question__link question__link--external" :href="question.link.url" target="_blank">
          <RichTextRenderer :html="generalSettings.customLabels.linkLabel" />
        </a>
      </h5>
      <div
        v-if="
          generalSettings.showAnswerButton ||
          question.enableMultipleAnswers ||
          showPreviousLink ||
          question.sliderAnswerButton?.enabled
        "
        class="content"
      >
        <div class="content__item content__item--action">
          <RichTextRenderer v-if="isError" tag="p" class="content__item-action-error" :html="getCustomLabel" />
          <UIButton
            v-if="
              (generalSettings.showAnswerButton ||
                question.enableMultipleAnswers ||
                question.sliderAnswerButton?.enabled) &&
              question.answerType !== QuizAnswerTypes.SWIPE
            "
            :id="`game-button-step-${activeQuestionIndex}`"
            :tabindex="hasTabIndex ? 0 : -1"
            :disabled="disableAnswer || isError"
            :loading="isLoading"
            @click="onQuestionAnswer"
          >
            <RichTextRenderer tag="span" :html="buttonLabel" class="content__item-action-text" />
          </UIButton>
          <div v-if="showPreviousLink" class="question__back">
            <a @click.prevent="onGoToPreviousQuestion">
              {{ applyReplacementTags(previousButtonLabel) }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { getCurrentInstance, nextTick } from 'vue';
import { computed, defineComponent, ref, watch } from 'vue';
import type { GameQuizAnswerState, GameQuizQuestionState, GameQuizGeneralState } from './Model';
import { QuizAnswerTypes, QuizVideoPositionTypes } from './Model';
import QuizAnswersItems from './AnswersItems.vue';
import { SdkQuizQuestionDisplayEvent } from './sdk/events/question-display';
import { SdkQuizQuestionAnswerEvent } from './sdk/events/question-answer';
import { SdkQuizQuestionModel } from './sdk/question-model';
import { SdkQuizQuestionAnswerModel } from './sdk/question-answer-model';
import {
  applyReplacementTags,
  generateVimeoUrl,
  generateYouTubeUrl,
  getVimeoIdFromUrl,
  getYouTubeVideoIdFromUrl,
  preloadImagePromise
} from '@/src/utilities/Utilities';
import type { ClassList } from '@/src/typings/types/types';
import RichTextRenderer from '@/src/components/RichTextRenderer.vue';
import UIButton from '@/src/components/ui/UIButton.vue';
import { addDataLayerEvent } from '@/src/utilities/Tracking';
import { useCampaignStore } from '@/src/store/campaign';
import { useUtilityStore } from '@/src/store/utility';
import { recursivelyWaitForPromises } from '@/src/utilities/virtualDom';
import { SdkGameEventsEmitter } from '@/src/sdk/api/game/events';

export default defineComponent({
  name: 'QuizQuestion',
  components: { UIButton, QuizAnswersItems, RichTextRenderer },
  props: {
    question: {
      type: Object as PropType<GameQuizQuestionState>,
      required: true
    },
    generalSettings: {
      type: Object as PropType<GameQuizGeneralState>,
      required: true
    },
    initialSelectedAnswer: {
      type: Object as PropType<GameQuizAnswerState[]>,
      required: false
    },
    previousButtonLabel: {
      type: String,
      default: 'Previous'
    },
    activeQuestionIndex: {
      type: Number,
      required: true
    },
    loading: {
      type: Boolean,
      required: true
    },
    /**
     * Exposes a button for the user to be able to navigate back to last question.
     */
    isQuestionLast: {
      type: Boolean,
      required: true
    }
  },
  emits: ['previousQuestion', 'questionAnswer', 'stopTimer'],
  setup: (props, context) => {
    /**
     * ---- VARIABLES -----
     **/
    const campaignStore = useCampaignStore();
    const utilityStore = useUtilityStore();

    const selectedAnswers = ref<GameQuizAnswerState[]>(props.initialSelectedAnswer ?? []);
    const isError = ref(false);
    const disableAnswer = ref(false);
    const showOthersAnswers = ref(false);
    const showTheCorrectAnswer = ref(false);
    const originUrl = window.location.href;
    const goToPreviousQuestion = ref(false);
    const isLoading = ref(false);
    const showOtherPeopleAnswerButton = ref(false);

    let readyPromiseResolve: (() => void) | undefined;
    const readyPromise = new Promise<void>((resolve) => {
      readyPromiseResolve = resolve;
    });

    // remove previous step
    utilityStore.removeBodyClass(`current-game-step-${props.activeQuestionIndex}`);

    // add current step
    utilityStore.addBodyClass(`current-game-step-${props.activeQuestionIndex + 1}`);

    addDataLayerEvent('game_page', {
      questionIndex: props.activeQuestionIndex + 1
    });

    /**
     * ---- COMPUTED -----
     **/

    const isFocusable = computed(() => {
      return campaignStore.model?.hasFocusStyle();
    });

    const showPreviousLink = computed(() => {
      if (props.generalSettings.goBackwards?.enabled) {
        if (isError.value) {
          return false;
        }
        return !(props.activeQuestionIndex === 0 && !disableAnswer.value);
      }
      return false;
    });
    const getCustomLabel = computed(() => {
      if (
        props.question.minAnswersValidationMessage &&
        props.question.minAnswers &&
        props.question.minAnswers > selectedAnswers.value.length
      ) {
        return props.question.minAnswersValidationMessage;
      }

      if (
        props.question.maxAnswersValidationMessage &&
        props.question.maxAnswers &&
        props.question.maxAnswers <= selectedAnswers.value.length
      ) {
        return props.question.maxAnswersValidationMessage;
      }
      return props.generalSettings.customLabels.buttons.selectAnswer;
    });

    const iframeSource = computed(() => {
      if (props.question.video.url) {
        const getYoutubeVideoID = getYouTubeVideoIdFromUrl(props.question.video.url);
        const getVimeoVideoID = getVimeoIdFromUrl(props.question.video.url);
        if (getYoutubeVideoID) {
          return `${generateYouTubeUrl(getYoutubeVideoID)}&origin=${originUrl}`;
        }

        if (getVimeoVideoID) {
          return generateVimeoUrl(getVimeoVideoID);
        }
      }
      return undefined;
    });

    const isEditModeActive = computed(() => {
      return campaignStore.model?.state.isEditModeActive;
    });

    const hasTabIndex = computed(() => {
      if (!isEditModeActive.value) {
        return true;
      }
      return false;
    });

    const showIframe = computed(() => {
      return props.question.video.enabled && props.question.video.url;
    });

    const questionFrameWrapperStyling = computed(() => {
      return {
        width: `${props.question.video.width}px`
      };
    });

    const questionContainerClasses = computed(() => {
      const classes: ClassList = {};

      classes['question--has-error'] = isError.value;
      classes[`question-${campaignStore.model?.id}`] = true;
      classes[`question--index-${props.activeQuestionIndex}`] = true;
      classes[`question--question-index-${props.activeQuestionIndex + 1}`] = true;
      classes[`question--answers-${props.question.answers.length}`] = true;
      classes['question--images-loaded'] = true;

      if (props.question.video.enabled && props.question.video.url) {
        classes['question--has-video'] = true;
      }
      if (showOthersAnswers.value) {
        classes['question--result'] = true;
      }

      classes['question--disabled'] = disableAnswer.value;

      if (showOtherPeopleAnswerButton.value) {
        classes['question--disabled'] = true;
        classes['question--awaiting-continue'] = true;
      }

      return classes;
    });

    const videoContentClasses = computed(() => {
      let position = 'above';
      const classes: ClassList = {};

      switch (props.question.video.position) {
        case QuizVideoPositionTypes.LEFT:
          position = 'left';
          break;

        case QuizVideoPositionTypes.RIGHT:
          position = 'right';
          break;

        default:
          position = 'above';
      }

      if (props.question.video.enabled && props.question.video.url) {
        classes['question__content--has-video'] = true;
        classes[`question__content--has-video-${position}`] = true;
        classes['has-video'] = true;
      }

      return classes;
    });

    const buttonLabel = computed(() => {
      if (showOtherPeopleAnswerButton.value && props.question.otherPeoplesAnswerButton) {
        return props.question.otherPeoplesAnswerButton;
      }

      if (props.question.questionButtonLabel) {
        return props.question.questionButtonLabel;
      }

      if (props.question.sliderAnswerButton?.enabled && props.question.sliderAnswerButton?.label) {
        return props.question.sliderAnswerButton.label;
      }

      if (props.isQuestionLast && !props.question.feedback.enabled) {
        return props.generalSettings.customLabels?.buttons.lastAnswer
          ? props.generalSettings.customLabels?.buttons.lastAnswer
          : props.generalSettings.customLabels?.buttons.answer;
      }

      return applyReplacementTags(props.generalSettings.customLabels?.buttons.answer || 'Answer');
    });

    /**
     * ---- METHODS -----
     **/

    const onSubmitAnswer = (forceSubmit = false) => {
      // swipeit dosn't have any button so we auto submit if swipeit
      if (
        (!props.generalSettings.showAnswerButton &&
          !props.question.enableMultipleAnswers &&
          !props.question.sliderAnswerButton?.enabled) ||
        props.question.swipeImage ||
        forceSubmit
      ) {
        onQuestionAnswer();
      }
    };

    const onAnswerSelected = (answer: GameQuizAnswerState) => {
      if (disableAnswer.value || showOtherPeopleAnswerButton.value) {
        return;
      }

      const answerExists = selectedAnswers.value.some((el) => el.id === answer.id);

      // if only one answer is allow - remove the last one and push a new
      if (!props.question.enableMultipleAnswers) {
        selectedAnswers.value.pop();
        selectedAnswers.value.push(answer);
      }

      // if multiple answers
      if (props.question.enableMultipleAnswers) {
        let reachedMaxAnswers = false;

        // checking for max allowed answers
        if (
          props.question.maxAnswers &&
          props.question.maxAnswers !== 0 &&
          props.question.maxAnswers <= selectedAnswers.value.length
        ) {
          reachedMaxAnswers = true;

          /** If too many options is chosen, show message to user */
          const wait = 1200;
          isError.value = true;
          window.setTimeout(() => {
            isError.value = false;
            reachedMaxAnswers = false;
            disableAnswer.value = false;
          }, wait); // Clear error after 1200 ms
        }
        // if answer exists then we remove it from our array of answer
        if (answerExists) {
          selectedAnswers.value = selectedAnswers.value.filter((answerItem) => {
            return answerItem.id !== answer.id;
          });
          // if answer dosnt exits and it dosnt reached the max allowed answers count
          // then we oush it to our question answers array
        } else if (!reachedMaxAnswers) {
          selectedAnswers.value.push(answer);
        }
      }

      onSubmitAnswer();
    };

    const onQuestionAnswer = async () => {
      disableAnswer.value = true;
      let wait = 100;

      // if no answer selected return and have an error.
      if (selectedAnswers.value.length <= 0) {
        wait = 1000;
        isError.value = true;
        window.setTimeout(() => {
          isError.value = false;
          isLoading.value = false;
          disableAnswer.value = false;
        }, wait); // Clear error after 1200 ms
        return;
      }

      if (
        props.question.enableMultipleAnswers &&
        props.question?.minAnswers &&
        props.question.minAnswers > selectedAnswers.value.length
      ) {
        wait = 1000;
        isError.value = true;
        window.setTimeout(() => {
          isError.value = false;
          isLoading.value = false;
          disableAnswer.value = false;
        }, wait); // Clear error after 1200 ms
        return;
      }

      if (props.generalSettings.showCorrectAnswer && props.question.answerType !== QuizAnswerTypes.SWIPE) {
        wait = 1500;
        showTheCorrectAnswer.value = true;
      }

      if (
        props.question.enablePeopleAnswers &&
        props.question.answerType !== QuizAnswerTypes.SWIPE &&
        !showOtherPeopleAnswerButton.value
      ) {
        showOthersAnswers.value = true;
        // delay - have no settings for wait time.
        wait = 3000;
      }

      context.emit('stopTimer');

      // Validate if user answered correctly or not
      const correctAnswer =
        selectedAnswers.value.filter((answerItem) => props.question.correctAnswers.includes(answerItem.id)).length ===
        selectedAnswers.value.length;

      // Emit SDK question answer event, allowing external implementations to peak in what was
      // answered and suspense the game if needed.
      const quizQuestionAnswerEvent = new SdkQuizQuestionAnswerEvent(
        new SdkQuizQuestionModel(props.question, props.isQuestionLast),
        selectedAnswers.value.map((answer) => new SdkQuizQuestionAnswerModel(props.question, answer)),
        correctAnswer
      );

      SdkGameEventsEmitter.emit('question-answer', quizQuestionAnswerEvent);

      if (
        props.question.enablePeopleAnswers &&
        props.question.otherPeoplesAnswerButton &&
        !showOtherPeopleAnswerButton.value
      ) {
        setTimeout(() => {
          isError.value = false;
          isLoading.value = false;
          disableAnswer.value = false;
          showOtherPeopleAnswerButton.value = true;
        }, wait);

        return;
      }

      if (props.isQuestionLast && !showOtherPeopleAnswerButton.value) {
        isLoading.value = true;
      }

      if (quizQuestionAnswerEvent.hasSuspense) {
        await quizQuestionAnswerEvent.waitForPromises();

        if (goToPreviousQuestion.value) {
          return;
        }

        onSubmit(selectedAnswers.value);
      } else {
        setTimeout(() => {
          if (goToPreviousQuestion.value) {
            return;
          }

          onSubmit(selectedAnswers.value);
        }, wait);
      }
    };

    const onGoToPreviousQuestion = () => {
      goToPreviousQuestion.value = true;
      context.emit('previousQuestion');
    };

    const onSubmit = (answer: GameQuizAnswerState[]) => {
      if (props.question.enablePeopleAnswers) {
        showOthersAnswers.value = false;
      }

      context.emit('questionAnswer', answer, props.question);
    };

    const currentInstance = getCurrentInstance();

    (async () => {
      if (props.question.backgroundImage?.enabled && props.question.backgroundImage.src) {
        await preloadImagePromise(props.question.backgroundImage.src).then(() => {
          if (props.question.backgroundImage?.enabled) {
            campaignStore.gameSectionImageOverlay = props.question.backgroundImage;
          } else {
            campaignStore.gameSectionImageOverlay = undefined;
          }
        });
      }

      // Wait for dependency components to load before emitting event that question is displayed
      if (currentInstance?.vnode) {
        await recursivelyWaitForPromises(currentInstance.vnode, 'onBeforeEnter');
      }

      if (readyPromiseResolve) {
        readyPromiseResolve();
      }

      nextTick(() => {
        SdkGameEventsEmitter.emit(
          'question-display',
          new SdkQuizQuestionDisplayEvent(new SdkQuizQuestionModel(props.question, props.isQuestionLast))
        );
      });
    })();

    watch(
      () => props.loading,
      (value) => {
        isLoading.value = value;
        disableAnswer.value = value;
      }
    );

    return {
      hasTabIndex,
      onSubmitAnswer,
      isFocusable,
      disableAnswer,
      showIframe,
      iframeSource,
      originUrl,
      getCustomLabel,
      showOthersAnswers,
      showTheCorrectAnswer,
      getYouTubeVideoIdFromUrl,
      showPreviousLink,
      questionContainerClasses,
      videoContentClasses,
      onGoToPreviousQuestion,
      onQuestionAnswer,
      QuizAnswerTypes,
      buttonLabel,
      isLoading,
      onAnswerSelected,
      selectedAnswers,
      isError,
      questionFrameWrapperStyling,
      applyReplacementTags,
      onBeforeEnter: async () => {
        await readyPromise;
      }
    };
  }
});
</script>
